/* global document, HTMLElement */

class MainNavigationDrawer extends HTMLElement {
  constructor() {
    super();
    this.triggerButtons = [];
    this.openClass = "open";
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onLinkClick = this.onLinkClick.bind(this);
    this.onToggle = this.onToggle.bind(this);
  }

  connectedCallback() {
    this.triggerButtons = [...document.querySelectorAll(".mnd__btn")];
    for (const button of this.triggerButtons) {
      button.addEventListener("click", this.onTriggerClick);
      button.setAttribute("aria-haspopup", "dialog");
      button.setAttribute("aria-controls", this.id);
      button.setAttribute("aria-expanded", "false");
    }

    this.closeButtons = [
      ...this.querySelectorAll("[data-main-navigation-drawer-close]"),
    ];
    for (const button of this.closeButtons) {
      button.addEventListener("click", this.onCloseClick);
    }

    this.navigationLinks = [
      ...this.querySelectorAll(
        "a[href]:not([href^='#']):not([target='_blank'])",
      ),
    ];
    for (const link of this.navigationLinks) {
      link.addEventListener("click", this.onLinkClick);
    }

    this.details = [
      ...this.querySelectorAll(".main-navigation-drawer__details"),
    ];
    for (const details of this.details) {
      details.addEventListener("toggle", this.onToggle);
    }
  }

  disconnectedCallback() {
    for (const button of this.triggerButtons) {
      button.removeEventListener("click", this.onTriggerClick);
    }
    for (const button of this.closeButtons || []) {
      button.removeEventListener("click", this.onCloseClick);
    }
    for (const link of this.navigationLinks || []) {
      link.removeEventListener("click", this.onLinkClick);
    }
    for (const details of this.details || []) {
      details.removeEventListener("toggle", this.onToggle);
    }
    document.removeEventListener("keydown", this.onKeydown);
  }

  onTriggerClick(event) {
    event.preventDefault();

    if (this.classList.contains(this.openClass)) {
      this.close();
      return;
    }

    this.open();
  }

  onCloseClick() {
    this.close();
  }

  onKeydown(event) {
    if (event.key === "Escape") this.close();
  }

  onLinkClick(event) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    this.close();
  }

  onToggle(event) {
    const activeDetails = event.currentTarget;
    if (!activeDetails.open) return;

    for (const details of this.details) {
      if (details !== activeDetails) details.removeAttribute("open");
    }
  }

  open() {
    this.hidden = false;
    this.classList.add("opening");
    this.setTriggerState(true);
    document.documentElement.classList.add("main-navigation-drawer-opened");
    document.addEventListener("keydown", this.onKeydown);

    globalThis.requestAnimationFrame(() => {
      this.classList.add(this.openClass);
      this.classList.remove("opening");
      this.querySelector(".main-navigation-drawer__close")?.focus();
    });
  }

  close() {
    if (this.hidden) return;

    this.classList.add("closing");
    this.classList.remove(this.openClass);
    this.setTriggerState(false);
    document.documentElement.classList.remove("main-navigation-drawer-opened");
    document.removeEventListener("keydown", this.onKeydown);

    globalThis.setTimeout(() => {
      this.classList.remove("closing");
      this.hidden = true;
    }, 250);
  }

  setTriggerState(isOpen) {
    for (const button of this.triggerButtons) {
      button.setAttribute("aria-expanded", String(isOpen));
    }
  }
}

if (!globalThis.customElements.get("main-navigation-drawer")) {
  globalThis.customElements.define(
    "main-navigation-drawer",
    MainNavigationDrawer,
  );
}
